/**
 * Bounded Context: Pyodide Bridge — Vue composable
 *
 * State machine: idle | loading | ready | error
 * - idle: nenhum turno enviado ainda
 * - loading: Pyodide inicializando OU processando turno
 * - ready: último turno processado com sucesso
 * - error: ConstitutionalError ou TechnicalError
 *
 * Constitucional:
 * - Erro handling gracioso (CC-05: zero penalidade)
 * - R = -∞ → silent (Princípio X: dignidade)
 * - Privacy boundary: zero PII em logs (CL-08)
 */

import { ref } from "vue";
import {
  initPyodide,
  processTurn,
  type PyodideLike,
} from "../pyodide/bridge";
import {
  ConstitutionalViolationError,
  type ProcessTurnError,
  type ProcessTurnResult,
  type ProcessTurnState,
  type UserTurn,
} from "../pyodide";

export { ConstitutionalViolationError };

export function useProcessTurn() {
  /** Estado da state machine. */
  const state = ref<ProcessTurnState>("idle");
  /** Resultado do último turno (null quando idle/error). */
  const lastResult = ref<ProcessTurnResult | null>(null);
  /** Erro do último turno (null quando idle/ready). */
  const lastError = ref<ProcessTurnError | null>(null);
  /** Instância Pyodide (lazy + cached). */
  let pyodide: PyodideLike | null = null;

  /**
   * Garante que Pyodide está inicializado (lazy + cached).
   */
  async function ensurePyodide(): Promise<PyodideLike> {
    if (pyodide) return pyodide;
    state.value = "loading";
    try {
      pyodide = await initPyodide();
      state.value = "ready";
      return pyodide;
    } catch (e) {
      state.value = "error";
      lastError.value = {
        kind: "technical",
        message: e instanceof Error ? e.message : String(e),
      };
      throw e;
    }
  }

  /**
   * Processa um turno do aprendiz via kernel Python.
   */
  async function processUserTurn(turn: UserTurn): Promise<ProcessTurnResult> {
    state.value = "loading";
    lastError.value = null;
    try {
      const py = await ensurePyodide();
      const result = await processTurn(py, turn);
      lastResult.value = result;
      state.value = "ready";
      return result;
    } catch (e) {
      // ConstitutionalViolationError → silent (R = -∞).
      if (e instanceof ConstitutionalViolationError) {
        const silent: ProcessTurnResult = {
          reply: "Desculpe, não posso processar essa solicitação.",
          rLevel: "R3",
          initiativeText: null,
          curiosityGaps: [],
        };
        lastResult.value = silent;
        lastError.value = {
          kind: "constitutional",
          violations: e.violations,
          reply: silent.reply,
        };
        state.value = "ready";
        return silent;
      }
      // Erro técnico.
      state.value = "error";
      lastError.value = {
        kind: "technical",
        message: e instanceof Error ? e.message : String(e),
      };
      throw e;
    }
  }

  /** Reseta estado (para novo turno ou para limpar erro). */
  function reset(): void {
    state.value = "idle";
    lastResult.value = null;
    lastError.value = null;
  }

  return {
    /** Refs reativas. */
    state,
    lastResult,
    lastError,
    /** Ações. */
    processUserTurn,
    reset,
  };
}
